/**
 * Exposes live (pending/claimed) `grading`, `languageUseGroupGrading` and `gradingConsistency` job rows to
 * the client, so the grading canvas can show a per-(submission, task/scheme, dimension) loading overlay
 * instead of a class-wide flag. Each `grading`/`languageUseGroupGrading` row already carries exactly that
 * scope in its payload (see GradingPayload / LanguageUseGroupGradingPayload in ./types) — this file only
 * reads it back, it starts no new work.
 *
 * `gradingConsistency` is the odd one out: its payload (see GradingConsistencyPayload) carries a `taskKey`
 * but no `submissionId` — it reconciles one task's grades across every submission in the wave at once, so
 * it cannot name one. A live row therefore means "this task's content grade might still change for any
 * submission that was part of the wave," not one specific submission — the client fans it out to every
 * submission it currently knows about (see chat.tsx), the same coarser-than-precise tradeoff the MS/GS
 * extraction spinner already accepts for the same reason (see get-live-solution-jobs.ts).
 *
 * Rows are handed to the client as JSON strings rather than a registered Entity: the shape is a pure,
 * ephemeral read of `exam_jobs` with no persistence semantics of its own, the same reasoning that already
 * justifies MasterSolutionDTO's `languageUseGroupsJson` field.
 */
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { queryLiveJobRows, watchLiveJobRows } from './live-job-rows.js';

const LIVE_GRADING_JOB_TYPES = ['grading', 'languageUseGroupGrading', 'gradingConsistency'] as const;

/** One live job row, scoped to what a canvas box needs to know. See GradingPayload / LanguageUseGroupGradingPayload
 * / GradingConsistencyPayload. */
export interface LiveGradingJobRow {
  type: (typeof LIVE_GRADING_JOB_TYPES)[number];
  /**
   * QUEUED vs ACTUALLY RUNNING. "Live" is `pending` OR `claimed` (see `liveJobFilter`), and collapsing the two
   * made every box of a wave claim to be grading the moment it was dispatched — a whole cohort's worth of
   * spinners for the ≤`JOB_CONCURRENCY` rows that can actually be running. Projected so the canvas can say
   * "in der Warteschlange" for the rest instead of overstating what is happening.
   */
  status?: 'pending' | 'claimed';
  /** `grading`/`languageUseGroupGrading` only — absent for `gradingConsistency` (see the module doc). */
  submissionId?: string;
  /** `grading`/`gradingConsistency` — the main task's root id (getTaskKey), matching MasterSolutionDTO/exam.tasks ids. */
  taskKey?: string;
  /**
   * The refs WITHIN that main task this row actually regrades, when the request was finer than the task
   * (`GradingPayload.taskRefs` — e.g. `['1.1a.i']`). Absent means the whole main task.
   *
   * Projected because `taskKey` alone is the main task for a subtask-scoped regrade too, so a canvas keying on
   * it lit up every sibling under that root while only the named subtask was being regraded.
   */
  taskRefs?: string[];
  /** `grading` only — 'content' (default/absent) or 'languageUse' (a solo rule riding this same row). */
  target?: string;
  /** The LanguageUseScheme id this row grades — from `languageUseGroupGrading`'s payload, or from a
   * `grading` row's `languageUseSchemeId` when it scopes a multi-member group's content pass. */
  schemeId?: string;
}

function toRow(doc: any): LiveGradingJobRow {
  const payload = doc?.payload ?? {};
  return {
    type: doc?.type,
    ...(doc?.status === 'pending' || doc?.status === 'claimed' ? { status: doc.status } : {}),
    ...(payload.submissionId ? { submissionId: String(payload.submissionId) } : {}),
    ...(payload.taskKey ? { taskKey: String(payload.taskKey) } : {}),
    ...(Array.isArray(payload.taskRefs) && payload.taskRefs.length
      ? { taskRefs: (payload.taskRefs as unknown[]).map(String) }
      : {}),
    ...(payload.target ? { target: String(payload.target) } : {}),
    ...(payload.schemeId
      ? { schemeId: String(payload.schemeId) }
      : payload.languageUseSchemeId
        ? { schemeId: String(payload.languageUseSchemeId) }
        : {}),
  };
}

export const getLiveGradingJobsQuery = registerQuery({
  operationName: 'getLiveGradingJobsQuery',
  type: () => [String],
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => async () => queryLiveJobRows(examId, LIVE_GRADING_JOB_TYPES, toRow),
});

export const streamLiveGradingJobsByExamIdSubscription = registerSubscription({
  operationName: 'streamLiveGradingJobsByExamIdSubscription',
  type: () => [String],
  params: [{ name: 'examId', type: () => String }],
  resolve: (examId: string) => () => watchLiveJobRows(examId, LIVE_GRADING_JOB_TYPES, toRow),
});
